<!-- HTMLDescription.svelte -->
<script lang="ts">
	import { browser } from '$app/environment'
	import { sanitizeHtml } from '$src/lib/utils/sanitize'
	import { twMerge } from 'tailwind-merge'
	import SkeletonBlock from '../loading/SkeletonBlock.svelte'
	import { override } from '$src/lib/utils/classnames'

	interface Props {
		description: string
		isPublic?: boolean
		forceExpand?: boolean
		maxLength?: number
		class?: string
	}

	let {
		description,
		isPublic = false,
		forceExpand = false,
		maxLength = 100,
		class: className = '',
	}: Props = $props()

	let isExpanded = $state(false)
	let shouldClamp = true
	let element: HTMLDivElement = $state()

	function trimDescription(desc: string): string {
		const trimmed = desc?.trim() ?? ''
		return isPublic && trimmed.length > maxLength ? trimmed.slice(0, maxLength) + ' ...' : trimmed
	}

	let sanitizedContent = $state('')
	let contentLoaded = $derived(sanitizedContent !== '')

	$effect(() => {
		if (!browser || description == null) return

		let cancelled = false

		sanitizeHtml(trimDescription(description), browser, (result) => {
			if (!cancelled) {
				sanitizedContent = result
			}
		})

		return () => {
			cancelled = true
		}
	})

	function handleExpand(event: MouseEvent | TouchEvent | KeyboardEvent, expand: boolean) {
		if (isPublic) return
		event.stopPropagation()
		isExpanded = expand
	}

	let computedClassName = $derived(
		twMerge(override('flex flex-col items-start w-full pb-4 mt-3', className)),
	)
</script>

<div class={computedClassName} data-testid="description-container">
	{#if !contentLoaded}
		<div class="w-full space-y-2 pb-2">
			<SkeletonBlock width="100%" height="16px" />
			<SkeletonBlock width="90%" height="16px" />
			<SkeletonBlock width="95%" height="16px" />
		</div>
	{:else}
		<div class="relative w-full">
			<div
				bind:this={element}
				class="w-full overflow-hidden cursor-pointer text-left prose-content text-brand-gray-2"
				class:line-clamp-3={shouldClamp && !isExpanded && !forceExpand}
				onclick={(event) => handleExpand(event, true)}
				ontouchstart={(event) => handleExpand(event, true)}
				onkeydown={(e) => e.key === 'Enter' && handleExpand(e, true)}
				role="button"
				tabindex="0"
				data-testid="description-text"
			>
				{@html sanitizedContent}
			</div>

			{#if shouldClamp && !isExpanded && !forceExpand}
				<button
					disabled={isPublic}
					onclick={(event) => handleExpand(event, true)}
					ontouchstart={(event) => handleExpand(event, true)}
					onkeydown={(e) => e.key === 'Enter' && handleExpand(e, true)}
					class="absolute bottom-0 left-0 w-full h-12 bg-gradient-to-t from-brand-gray-5 to-transparent pointer-events-none"
				>
					<!-- save space -->
				</button>
			{/if}

			{#if shouldClamp && isExpanded && !forceExpand}
				<button
					disabled={isPublic}
					onclick={(event) => handleExpand(event, false)}
					ontouchstart={(event) => handleExpand(event, false)}
					onkeydown={(e) => e.key === 'Enter' && handleExpand(e, false)}
					class="mx-auto mt-4 bg-brand-gray-5 pointer-events-pointer dark:bg-brand-gray-4/10 border-brand-gray-4 dark:hover:bg-brand-gray-4/20 border dark:text-brand-gray-3 text-black transition-all px-3 py-1 text-xs rounded-md flex items-center"
				>
					hide description
				</button>
			{/if}
		</div>
	{/if}
</div>

<style global>
	.line-clamp-3 {
		display: -webkit-box;
		-webkit-line-clamp: 3;
		line-clamp: 3;
		-webkit-box-orient: vertical;
		overflow: hidden;
		position: relative;
	}

	/* Prose styling */
	.prose-content :global(p) {
		margin-bottom: 1em;
		line-height: 1.5;
	}

	.prose-content :global(h1) {
		font-size: 2em;
		margin-bottom: 0.5em;
		font-weight: 600;
	}

	.prose-content :global(h2) {
		font-size: 1.5em;
		margin-bottom: 0.5em;
		font-weight: 600;
	}

	.prose-content :global(h3) {
		font-size: 1.25em;
		margin-bottom: 0.5em;
		font-weight: 600;
	}

	/* Enhanced list styling */
	.prose-content :global(ul),
	.prose-content :global(ol) {
		margin: 1em 0;
		padding-left: 2em;
		list-style-position: outside;
	}

	.prose-content :global(ul) {
		list-style-type: disc;
	}

	.prose-content :global(ol) {
		list-style-type: decimal;
	}

	.prose-content :global(ul ul),
	.prose-content :global(ol ol),
	.prose-content :global(ul ol),
	.prose-content :global(ol ul) {
		margin: 0.5em 0;
	}

	.prose-content :global(ul ul) {
		list-style-type: circle;
	}

	.prose-content :global(ul ul ul) {
		list-style-type: square;
	}

	.prose-content :global(li) {
		margin: 0.5em 0;
		padding-left: 0.5em;
		line-height: 1.5;
	}

	.prose-content :global(li::marker) {
		color: #666;
	}

	.prose-content :global(li > p) {
		margin: 0.5em 0;
	}

	.prose-content :global(blockquote) {
		border-left: 3px solid #e2e8f0;
		padding-left: 1em;
		margin: 1em 0;
		font-style: italic;
	}

	.prose-content :global(strong) {
		font-weight: 600;
	}

	.prose-content :global(em) {
		font-style: italic;
	}

	.prose-content :global(s) {
		text-decoration: line-through;
	}

	/* Remove bottom margin from last element */
	.prose-content :global(*:last-child) {
		margin-bottom: 0;
	}
</style>
